/**
 * The landing's sound valve — the same master switch the game header carries,
 * on the page a visitor meets first.
 *
 * The landing had no control at all: the only way to silence Fimbulwake was to
 * make an account and find the header toggle inside (QA 2026-08-18). The switch
 * writes the SAME persisted preference as the in-game one, so a choice made out
 * here is still honoured after login, and vice versa.
 *
 * AUTOPLAY: the landing never starts audio on its own. Browsers already refuse
 * sound before a gesture, but "the first click anywhere starts music" is barely
 * better on a marketing page. So the bed starts only when the visitor has said
 * yes at some point — this button, or the header toggle in a previous session
 * (`music.hasExplicitPreference()`). Never on a first visit.
 */
import * as music from '../game/music';
import * as sfx from '../game/sfx';
import { t } from '../i18n';

/** Speaker with waves / speaker crossed out — traced from the in-game header
 *  toggle so the two controls are visibly the same switch. */
const ICON_ON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
  + '<path d="M11 5 6 9H3v6h3l5 4V5z" fill="currentColor" stroke="none"/>'
  + '<path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.5 6a9 9 0 0 1 0 12"/></svg>';
const ICON_OFF =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
  + '<path d="M11 5 6 9H3v6h3l5 4V5z" fill="currentColor" stroke="none"/>'
  + '<line x1="16" y1="9" x2="22" y2="15"/><line x1="22" y1="9" x2="16" y2="15"/></svg>';

/**
 * Sound is audible on the landing only when the visitor has explicitly said yes.
 *
 * The second half is the in-game header's own reading (`music.isEnabled() ||
 * !sfx.isMuted()`) rather than an AND: Settings keeps per-channel switches, and
 * a player with music on but sfx off has NOT silenced the game — reading it as
 * AND showed the valve closed while the bed was playing.
 */
const isOn = () =>
  music.hasExplicitPreference() && (music.isEnabled() || !sfx.isMuted());

/**
 * Build the toggle into every `.snd-pick` mount (the beam keeps one, the mobile
 * drawer another) and keep them in step. Returns a teardown.
 */
export function mountSoundToggle(root: HTMLElement): () => void {
  const mounts = [...root.querySelectorAll<HTMLElement>('.snd-pick')];
  const buttons: HTMLButtonElement[] = [];

  const paint = () => {
    const on = isOn();
    for (const b of buttons) {
      b.innerHTML = on ? ICON_ON : ICON_OFF;
      b.classList.toggle('is-off', !on);
      b.setAttribute('aria-pressed', String(on));
      const label = t(on ? 'heroScreen.soundOff' : 'heroScreen.soundOn');
      b.setAttribute('aria-label', label);
      b.title = label;
    }
  };

  for (const mount of mounts) {
    mount.innerHTML = '';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'snd-btn';
    // The click IS the gesture the autoplay policy wants, so turning sound on
    // here starts the bed immediately rather than waiting for another tap.
    btn.addEventListener('click', () => {
      const on = !isOn();
      music.setEnabled(on);
      sfx.setMuted(!on);
      paint();
    });
    mount.append(btn);
    buttons.push(btn);
  }

  paint();
  // Only for a visitor who already said yes — see the autoplay note above.
  if (isOn()) music.armOnFirstGesture();

  return () => buttons.forEach((b) => b.remove());
}

/** Re-label after a language switch (the landing refills copy in place). */
export function relabelSoundToggle(root: HTMLElement): void {
  const on = isOn();
  const label = t(on ? 'heroScreen.soundOff' : 'heroScreen.soundOn');
  root.querySelectorAll<HTMLButtonElement>('.snd-btn').forEach((b) => {
    b.setAttribute('aria-label', label);
    b.title = label;
  });
}
